import React, { useEffect, useState } from 'react';
import { createSelector } from '@reduxjs/toolkit';
import { ModalBody } from 'react-bootstrap';
import styled from 'styled-components';
import { format } from 'date-fns';

import type {
  IResourceClientMember,
  IResourceCreateAnnouncement,
  IResourceCreateDocument,
  IResourceCreateLink,
} from '@/api/types';
import type { IDeleteResourceModalConfig, IEditResourceModalData, IResourceModalConfig } from '@/hooks/types';
import type { ITreeFolder } from '@/components/common';
import type { IAnnouncementEditForm, IDocumentEditForm, ILinkEditForm } from './types';
import type { IAgentPosition, ISelectedFolder } from '@/store/types';
import type { AppDispatch, RootState } from '@/store';

import {
  selectFoldersList,
  selectPendingAction as selectResourcesPendingAction,
} from '@/store/selectors/resourcesSelectors';
import { useAppDispatch, useAppSelector, useResourceNavigation, useTimezone } from '@/hooks';
import { combineDateTimeToISO, isResourceAnnouncementItem } from '@/helpers';
import {
  createAnnouncementItem,
  createDocumentItem,
  createLinkItem,
  getMemberList,
  getResourceDetails,
  updateAnnouncementItem,
  updateDocumentItem,
  updateLinkItem,
} from '@/store/slices/resourcesSlice';
import { AnnouncementForm, DocumentForm, LinkForm } from '.';
import { selectAgentPositionPendingAction, selectAgentPositions } from '@/store/selectors/agentPositionSelectors';
import { getAgentPositionListByClientId } from '@/store/slices/pub/agentPositionSlice';
import {
  combineAgentPositionsFromClient,
  combineAvailableAgentPositionsFromClient,
} from '@/store/combiners/agentPositionCombiners';
import { selectUserId } from '@/store/selectors/authSelectors';
import { ResourceModalType } from '@/hooks/enums';
import { LoaderWrapper } from '@/components/common/loaders/LoaderWrapper';
import { combineClientMemberList } from '@/store/combiners/resourcesCombiners';
import { ResourceModalSkeleton } from './ResourceModal.skeleton';
import { YEAR_MONTH_DAY_DASH_FORMAT } from '@/config/variables';

const transformLinkFormData = (data: ILinkEditForm, timezoneName: string): IResourceCreateLink => {
  const selectedFolder = JSON.parse(data.folder) as { id: number; clientId: number };

  return {
    label: data.name,
    url: data.url,
    description: data.description,
    dateStart: combineDateTimeToISO(data.startDate, data.startTime, timezoneName),
    visibility: data.visibility,
    folder: selectedFolder.id,
    client: selectedFolder.clientId,
    createdBy: data.createdBy,
    ...(data.endDate && data.endTime && { dateEnd: combineDateTimeToISO(data.endDate, data.endTime, timezoneName) }),
  };
};

const transformAnnouncementFormData = (
  data: IAnnouncementEditForm,
  timezoneName: string,
): IResourceCreateAnnouncement => {
  const attachments = data.attachments.reduce<File[]>(
    (attachments, proxy) => (proxy.file ? [...attachments, proxy.file] : attachments),
    [],
  );
  const selectedFolder = JSON.parse(data.folder) as { id: number; clientId: number };

  return {
    title: data.title,
    body: data.body,
    dateStart: combineDateTimeToISO(
      format(new Date(data.startDate), YEAR_MONTH_DAY_DASH_FORMAT),
      data.startTime,
      timezoneName,
    ),
    dateEnd:
      (data.endDate &&
        data.endTime &&
        combineDateTimeToISO(format(new Date(data.endDate), YEAR_MONTH_DAY_DASH_FORMAT), data.endTime, timezoneName)) ||
      '',
    folder: selectedFolder.id,
    client: selectedFolder.clientId,
    notify: data.notify,
    stayOnTop: data.stayOnTop,
    type: data.type,
    everyone: data.everyone ?? false,
    createdBy: data.createdBy,
    ...(data.countries.length ? { countries: data.countries } : {}),
    ...(attachments.length ? { attachments } : {}),
    ...(data.recipients?.length ? { recipients: data.recipients.map(Number) } : {}),
  };
};

const transformDocumentFormData = (data: IDocumentEditForm, timezoneName: string): IResourceCreateDocument => {
  const selectedFolder = JSON.parse(data.folder) as { id: number; clientId: number };

  return {
    name: data.name,
    description: data.description,
    dateStart: combineDateTimeToISO(data.startDate, data.startTime, timezoneName),
    dateEnd: (data.endDate && data.endTime && combineDateTimeToISO(data.endDate, data.endTime, timezoneName)) || '',
    folder: selectedFolder.id,
    client: selectedFolder.clientId,
    file: data.file[0]?.file ?? null,
    path: data.file[0]?.path ?? null,
    createdBy: data.createdBy,
  };
};

const getResourceForm = (
  resourceModalConfig: Exclude<IResourceModalConfig, IDeleteResourceModalConfig>,
  handleClose: () => void,
  foldersList: ITreeFolder[],
  dispatch: AppDispatch,
  availablePositions: IAgentPosition[],
  timezoneName: string,
  clientMemberList: IResourceClientMember[],
  userId: number,
) => {
  const { data: modalData, modalType } = resourceModalConfig;

  switch (modalData.resourceType) {
    case 'link':
      return (
        <LinkForm
          parentFolderId={modalData.parentFolderId}
          clientId={modalData.clientId}
          onCancel={handleClose}
          foldersList={foldersList || []}
          existingResource={modalData.existingResource || undefined}
          clientMemberList={clientMemberList}
          userId={userId}
          onSubmit={async data => {
            handleClose();
            const payload = transformLinkFormData(data, timezoneName);
            await dispatch(
              modalType === 'new'
                ? createLinkItem(payload)
                : updateLinkItem({ ...payload, linkId: (modalData as IEditResourceModalData).resourceId }),
            );
          }}
        />
      );
    case 'announcement':
      return (
        <AnnouncementForm
          parentFolderId={modalData.parentFolderId}
          clientId={modalData.clientId}
          onCancel={handleClose}
          foldersList={foldersList || []}
          existingResource={modalData.existingResource || undefined}
          clientMemberList={clientMemberList}
          userId={userId}
          onSubmit={async data => {
            handleClose();
            const payload = transformAnnouncementFormData(data, timezoneName);

            await dispatch(
              modalType === 'new'
                ? createAnnouncementItem(payload)
                : updateAnnouncementItem({
                    ...payload,
                    announcementId: (modalData as IEditResourceModalData).resourceId,
                  }),
            );
          }}
          availablePositions={availablePositions}
        />
      );
    case 'document':
      return (
        <DocumentForm
          parentFolderId={modalData.parentFolderId}
          clientId={modalData.clientId}
          onCancel={handleClose}
          foldersList={foldersList || []}
          existingResource={modalData.existingResource || undefined}
          clientMemberList={clientMemberList}
          userId={userId}
          onSubmit={async data => {
            handleClose();
            const payload = transformDocumentFormData(data, timezoneName);

            await dispatch(
              modalType === 'new'
                ? createDocumentItem(payload)
                : updateDocumentItem({ ...payload, documentId: (modalData as IEditResourceModalData).resourceId }),
            );
          }}
        />
      );
    default:
      throw new Error('Missing database form.');
  }
};

const resourceModalSelector = createSelector(
  selectFoldersList,
  selectAgentPositions,
  selectUserId,
  selectAgentPositionPendingAction(getAgentPositionListByClientId.typePrefix),
  selectResourcesPendingAction(getMemberList.typePrefix),
  (_: RootState, { clientId, selectedFolder }: { clientId: number; selectedFolder: ISelectedFolder }) => ({
    clientId,
    selectedFolder,
  }),
  (
    foldersList,
    agentPositions,
    userId,
    isAgentPositionsRequestPending,
    isResourcesRequestPending,
    { selectedFolder, clientId },
  ) => ({
    foldersList,
    userId,
    isAgentPositionsRequestPending,
    isResourcesRequestPending,
    agentPositions: combineAgentPositionsFromClient(clientId, agentPositions),
    availablePositions: combineAvailableAgentPositionsFromClient(clientId, agentPositions),
    clientMemberList: combineClientMemberList(foldersList, selectedFolder),
  }),
);
interface IResourceModalProps {
  modalConfig: Exclude<IResourceModalConfig, IDeleteResourceModalConfig>;
  handleClose: () => void;
}

const ResourceModal = ({ modalConfig, handleClose }: IResourceModalProps): React.ReactElement => {
  const { timezoneName } = useTimezone();
  const { selectedFolder } = useResourceNavigation();
  const {
    foldersList,
    agentPositions,
    availablePositions,
    clientMemberList,
    userId,
    isAgentPositionsRequestPending,
    isResourcesRequestPending,
  } = useAppSelector(state => resourceModalSelector(state, { clientId: modalConfig.data.clientId, selectedFolder }));
  const dispatch = useAppDispatch();
  const [isFetchingResource, setIsFetchingResource] = useState(true);

  useEffect(() => {
    if (modalConfig.data.resourceType === 'announcement' && !agentPositions && !isAgentPositionsRequestPending) {
      dispatch(getAgentPositionListByClientId(modalConfig.data.clientId));
    }
  }, [
    agentPositions,
    dispatch,
    isAgentPositionsRequestPending,
    modalConfig.data.clientId,
    modalConfig.data.resourceType,
  ]);

  useEffect(() => {
    if (modalConfig.data.resourceType === 'announcement' && modalConfig.modalType === ResourceModalType.edit) {
      dispatch(getResourceDetails(modalConfig.data.existingResource))
        .unwrap()
        .then(resource => {
          if (isResourceAnnouncementItem(resource)) {
            modalConfig.data.existingResource = resource;
          }
        })
        .finally(() => setIsFetchingResource(false));
    } else {
      setIsFetchingResource(false);
    }
  }, [dispatch, modalConfig]);

  useEffect(() => {
    if (clientMemberList.length !== 0) {
      return;
    }

    const call = dispatch(getMemberList({ clientId: modalConfig.data.clientId }));
    return () => call.abort();
  }, [clientMemberList.length, dispatch, modalConfig.data.clientId]);

  if (!userId) return <></>;

  return (
    <StyledLoaderWrapper
      isLoading={isAgentPositionsRequestPending || isFetchingResource || isResourcesRequestPending}
      loader={<ResourceModalSkeleton resourceType={modalConfig.data.resourceType} />}
    >
      <ModalBody>
        {getResourceForm(
          modalConfig,
          handleClose,
          foldersList || [],
          dispatch,
          availablePositions || [],
          timezoneName,
          clientMemberList,
          userId,
        )}
      </ModalBody>
    </StyledLoaderWrapper>
  );
};

export { ResourceModal };

const StyledLoaderWrapper = styled(LoaderWrapper)`
  position: sticky;
  bottom: 0;
`;
